import { signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { StandardResponse } from '@sbd-doug-frontend/app/models/standardresponse.model';
import { GenericType } from '@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types';

export const businessTypes = signal<{label: string, value: number}[]>([
  { label: 'Individual or Sole Proprietorship', value: 3 },
  { label: 'Partnership', value: 2 },
  { label: 'Corporation', value: 4 },
  { label: 'LLC', value: 5 },
  { label: 'LLP', value: 6 },
]);

export enum IntakeFormStep {
  PrincipalSelection = 0,
  BondLookup = 1,
  RequestDetails = 2,
  AdditionalQuestions = 3,
  Notifications = 4,
  Confirmation = 5,
}

export enum CIPXIntakeFormPricingModel {
  Instant = "instant",
  Quote = "quote",
}

export enum CIPXQuoteRequestIntakeFormPrincipalSelectionType {
  New = "new",
  Existing = "existing",
  Myself = "myself",
}

export interface CIPXQuoteRequestIntakeForm {
  // Step-specific FormGroups
  principalSelection: FormGroup<CIPXQuoteRequestIntakeFormPrincipalSelectionStep>;
  bondLookup: FormGroup<CIPXQuoteRequestIntakeFormBondLookupStep>;
  requestDetails: FormGroup<CIPXQuoteRequestIntakeFormRequestDetailsStep>;
  communications: FormGroup<CIPXQuoteRequestIntakeFormCommunications>;

  // Shared/cross-step fields remain at top level
  additional_questions: FormGroup<CIPXQuoteRequestIntakeFormIntakeFormAdditionalQuestions>;
  additional_comments: FormControl<null | string>;
  state_bond_id: FormControl<null | number>;
  requested_coverage_begin_date: FormControl<null | string>;
  state_abbreviation: FormControl<null | string>;
  user_id: FormControl<null | string | number>;
}

export interface CIPXQuoteRequestIntakeFormPrincipalSelectionStep {
  principalSelectionType: FormControl<null | 'new' | 'existing' | 'myself'>;
  existingPrincipal: FormControl<null | string | GenericType>;
}

export interface CIPXQuoteRequestIntakeFormBondLookupStep {
  stateSelection: FormControl<null | string | GenericType>;
  bondFamilySelection: FormControl<null | string | CIPXQuoteRequestIntakeFormBondFamily>;
  stateBondSelection: FormControl<null | string | CIPXQuoteRequestIntakeFormStateBond>;
  selectedPenaltyOption: FormControl<null | CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption>;
  bond_amount: FormControl<null | number>;
  company_type_id: FormControl<null | number | string>;
  bond_family_label: FormControl<null | string>;
  surety_bond_user_notes: FormControl<null | string>;
}

export interface CIPXQuoteRequestIntakeFormRequestDetailsStep {
  addCompanyAddress: FormControl<null | boolean>;
  company_number_owners: FormControl<null | number>;
  company_formation_date: FormControl<null | string>;
  principal: FormGroup<CIPXQuoteRequestIntakeFormPrincipal>;
  owners: FormArray<FormGroup<CIPXQuoteRequestIntakeFormOwner>>;
}

export interface CIPXQuoteRequestIntakeFormPrincipal {
  company_name: FormControl<null | string>;
  company_dba: FormControl<null | string>;
  company_address_street_1: FormControl<null | string>;
  company_address_street_2: FormControl<null | string>;
  company_address_city: FormControl<null | string>;
  company_address_state_abbreviation: FormControl<null | string>;
  company_address_zip: FormControl<null | string>;
  company_address_county: FormControl<null | string>;
  company_address_phone: FormControl<null | string>;
}

export interface CIPXQuoteRequestIntakeFormOwner {
  owner_first_name: FormControl<null | string>;
  owner_middle_name: FormControl<null | string>;
  owner_last_name: FormControl<null | string>;
  owner_email: FormControl<null | string>;
  owner_phone: FormControl<null | string>;
  owner_ssn: FormControl<null | string>;
  owner_marital_status: FormControl<null | string>;
  owner_dob: FormControl<null | string>;
  owner_years_experience: FormControl<null | number>;
  owner_percent_owned: FormControl<null | number>;
  owner_title: FormControl<null | string>;
  owner_address_street_1: FormControl<null | string>;
  owner_address_street_2: FormControl<null | string>;
  owner_address_city: FormControl<null | string>;
  owner_address_state_abbreviation: FormControl<null | string>;
  owner_address_zip: FormControl<null | string>;
}

// Communications types - unified contacts array
export interface CIPXQuoteRequestIntakeFormCommunications {
  contacts: FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>>;
  use_same_for_renewals: FormControl<boolean>;
}

export enum CIPXQuoteRequestIntakeFormCommunicationsContactType {
  AccountUser = 'account_user',
  User = 'user',
  Listener = 'listener',
}

export type CIPXQuoteRequestIntakeFormCommunicationsContactTypeType = CIPXQuoteRequestIntakeFormCommunicationsContactType;

export interface CIPXQuoteRequestIntakeFormCommunicationsContact {
  contact_type: FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>;
  user_id: FormControl<number | null>;
  first_name: FormControl<null | string>;
  last_name: FormControl<null | string>;
  company_label: FormControl<null | string>;
  email: FormControl<null | string>;
  phone: FormControl<null | string>;
  sms_phone: FormControl<null | string>;
  email_enabled: FormControl<boolean>;
  phone_enabled: FormControl<boolean>;
  sms_enabled: FormControl<boolean>;
  renewal_email_enabled: FormControl<boolean>;
  renewal_phone_enabled: FormControl<boolean>;
  /** False when the contact has a known real email from the API (not a service email) — address must not be changed */
  email_editable: FormControl<boolean>;
}

export interface CIPXQuoteRequestIntakeFormCommunicationPreference {
  user_id: number;
  communication_setting_group_code: string;
  communication_setting_type_code: string;
  /** Audience bitmask: 1 = Customer, 2 = Account User (Me) */
  communication_setting_value?: number;
  /** Channel bitmask: 1 = Email, 2 = Phone, 4 = SMS */
  communication_channel_value: number;
  account_user_editable: boolean;
  user_first_name?: string;
  user_last_name?: string;
  user_email_address?: string;
  user_phone?: string;
  rh?: string;
}

export interface CIPXQuoteRequestIntakeFormPrincipalContact {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  user_email_address: string;
  user_phone: string;
  user_status: string;
  user_company_label?: string;
}

export interface CIPXQuoteRequestIntakeFormIntakeFormAdditionalQuestions {
  // Scalar answers are FormControls, address answers are FormGroups, and
  // collection question sets are FormArrays of per-item FormGroups.
  [key: string]: FormControl<any> | FormGroup<any> | FormArray<FormGroup<any>>;
}

export interface CIPXQuoteRequestIntakeFormBondFamiliesResponse
  extends StandardResponse {
  data: CIPXQuoteRequestIntakeFormBondFamiliesResponseData;
}

export interface CIPXQuoteRequestIntakeFormBondFamiliesResponseData {
  bond_families: {
    bond_families: CIPXQuoteRequestIntakeFormBondFamily[];
  }
}

export interface CIPXQuoteRequestIntakeFormBondFamily {
  bond_family_id: number;
  bond_classification_id: number;
  bond_family_label: string;
  grp: string;
  micro_description: string;
  slug: string;
  state_bond_family_label: string;
  state_micro_description: string;
  state_state_bond_family_id: number;
}


export interface CIPXQuoteRequestIntakeFormBondFamilyGroup {
  name: string;
  bondFamilies: CIPXQuoteRequestIntakeFormBondFamily[];
}

export interface CIPXQuoteRequestIntakeFormStateBondsResponse
  extends StandardResponse {
  data: CIPXQuoteRequestIntakeFormStateBondsResponseData;
}

export interface CIPXQuoteRequestIntakeFormStateBondsResponseData {
  state_bonds: {
    state_bonds: CIPXQuoteRequestIntakeFormStateBond[];
  }
}

export interface CIPXQuoteRequestIntakeFormStateBond {
  state_bond_id: number;
  surety_bond_label: string;
  bond_info: CIPXQuoteRequestIntakeFormStateBondBondInfo;

  requires_credit_check: boolean;
  requires_license_number: boolean;
  is_featured: boolean | null;
  surety_bond_id: number;
  bond_content: string | null;
  bond_family_id: string;
  state_abbreviation: string;
  bond_family_label: string;
  micro_description: string;
  mini_description: string;

  default_surety_bond_label: string;
  default_bond_family_id: string;
  default_requires_credit_check: string; // "0" or "1" in the JSON
  default_requires_license_number: string; // "0" or "1"
  default_sku: string;
  default_surety_bond_search_terms: string;

  term_type_id: number;
  term_type_label: string;
  underwriting_application_id: string | null;
  additional_questions?: CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionsSection[];
}

/**
 * Represents a section containing question sets
 */
export interface CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionsSection {
  /** The section identifier, e.g., "screening" */
  section: string;
  /** Optional display heading for the section, e.g. "Probate Court Information" */
  section_label?: string;
  /** Array of question sets within this section */
  question_sets: CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet[];
}

/**
 * Represents an individual question set.
 */
export interface CIPXQuoteRequestIntakeFormStateBondAdditionalQuestionSet {
  /** A code that identifies the question set */
  question_set_code: string;
  /** Contains layout and roles for the question set */
  question_set: CIPXQuoteRequestIntakeFormStateBondQuestionSet;
  /** A human-friendly label for the question set */
  question_set_label: string;
  /** Detailed questions for this set */
  questions: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion[];
}

/**
 * Describes the layout of a question set along with the roles associated with each question.
 */
export interface CIPXQuoteRequestIntakeFormStateBondQuestionSet {
  /** The section identifier, e.g., "screening" */
  section: string;
  /** Layout type, e.g. "screen_and_explain" or "collection" */
  layout: string;
  /** Cardinality and labeling for collection-layout sets */
  collection?: CIPXQuoteRequestIntakeFormStateBondQuestionSetCollection;
  /** Array of roles and question codes in this set */
  questions: CIPXQuoteRequestIntakeFormStateBondQuestionSetQuestion[];
}

/**
 * Collection metadata for question sets answered as a repeatable list
 * (e.g. related persons on probate bonds).
 */
export interface CIPXQuoteRequestIntakeFormStateBondQuestionSetCollection {
  min_items?: number;
  max_items?: number;
  /** Per-item heading, e.g. "Ward" rendered as "Ward 1", "Ward 2" */
  item_label?: string;
  /** Text for the add button, e.g. "Add another ward" */
  add_item_label?: string;
}

/**
 * A simplified question used inside the question_set for role mapping.
 */
export interface CIPXQuoteRequestIntakeFormStateBondQuestionSetQuestion {
  /** Role such as "screen" or "explain" */
  role: string;
  /** The corresponding question code */
  question_code: string;
  /** Optional override: text to display for the question */
  question_text?: string;
  /** Optional override: help text (maps to question_help_text on detailed question) */
  question_help_text?: string;
  /** Optional override: label to display for the question */
  question_label?: string;
  /** Optional override: placeholder to display for the question */
  question_placeholder?: string;
}

/**
 * A detailed question containing all the text, type, and validation information.
 */
export interface CIPXQuoteRequestIntakeFormStateBondDetailedQuestion {
  question_code: string;
  question_label: string;
  question_text: string;
  question_type: string;
  question_placeholder: string;
  question_help_text: string;
  question_error_text: string;
  /** Initial answer from question_info.default. Type matches the question. */
  default?: string | number | boolean | null;
  validation: CIPXQuoteRequestIntakeFormStateBondQuestionValidation;
  /** Optional user-chosen options configuration for question_type === 'user_selected_option' */
  user_selected_options?: {
    type: 'select' | 'radio';
    /** Display placeholder for the control */
    placeholder?: string;
    /** List of display strings (can be expanded to key/label later) */
    options: string[];
    /** Option pre-selected when the form is created */
    default?: string;
  };
  /** Optional display hints: control override and conditional visibility */
  display?: CIPXQuoteRequestIntakeFormStateBondQuestionDisplay;
}

export interface CIPXQuoteRequestIntakeFormStateBondQuestionDisplay {
  /** Control override, e.g. "textarea" */
  control?: string;
  /** Show this question only when a sibling answer matches */
  when?: {
    question_code: string;
    operator: 'eq' | 'neq';
    value: string;
  };
}

export interface CIPXQuoteRequestIntakeFormStateBondQuestionValidationArgument {
  // For dependency-based validation (e.g. for explainer questions)
  question_code?: string;
  // Value the dependent answer must equal to trigger the rule (defaults to truthy)
  assessed_value?: any;
  // For numeric min/max validation.
  min?: number;
  max?: number;
}

export interface CIPXQuoteRequestIntakeFormStateBondQuestionValidationRule {
  type: 'eq' | 'neq';
  source: string;
  assessed_value: any;
}

export interface CIPXQuoteRequestIntakeFormStateBondQuestionValidationSuccess {
  validation_rule: string;
  arguments?: CIPXQuoteRequestIntakeFormStateBondQuestionValidationArgument[];
}

export interface CIPXQuoteRequestIntakeFormStateBondQuestionValidationRuleItem {
  rule: CIPXQuoteRequestIntakeFormStateBondQuestionValidationRule;
  on_success: CIPXQuoteRequestIntakeFormStateBondQuestionValidationSuccess;
}

/**
 * Describes how a question is validated.
 */
export interface CIPXQuoteRequestIntakeFormStateBondQuestionValidation {
  validation_rule: string;
  arguments?: CIPXQuoteRequestIntakeFormStateBondQuestionValidationArgument[];
  min?: number;
  max?: number;
  rules?: CIPXQuoteRequestIntakeFormStateBondQuestionValidationRuleItem[];
}

export interface CIPXQuoteRequestIntakeFormStateBondBondInfo {
  options: CIPXQuoteRequestIntakeFormStateBondBondOption[];
  matrix: any;
  shipping: {
    default: {
      shipping_id: number;
    };
  };
}


/**
 * Each element inside `bond_info.options`.
 */
export interface CIPXQuoteRequestIntakeFormStateBondBondOption {
  /**
   * e.g. { years: 1 }
   * Some items might not have a `term` object, so mark as optional.
   */
  term?: {
    years: number;
  };

  /**
   * Some items have `issue.carrier_id`, `issue.delivery_plan_id`, etc.
   * Others may omit these fields. Hence optional properties.
   */
  issue?: {
    carrier_id: number;
    writing_company_id?: number;
    shipping_id?: number;
    delivery_plan_id?: number;
  };

  /**
   * Could be "fixed-amount", "fixed-percent", or "rules".
   * Some fields (like `amount`) only appear for "fixed-amount";
   * `percent` and `minimum_premium` only appear for "fixed-percent".
   */
  price?: {
    type: 'fixed-amount' | 'fixed-percent' | 'rules';
    amount?: number;
    percent?: number;
    minimum_premium?: number;
  };

  /**
   * Could be "fixed-amount" or "rules".
   * `amount` is used with "fixed-amount".
   * `rules` is an array of { amount: number; comparator: string }.
   */
  penalty?: {
    label: string;
    type: 'fixed-amount' | 'rules';
    amount?: number;
    rules?: Array<{
      amount: number;
      comparator: string;
    }>;
  };

  can_do_instant_download: boolean;
}

export interface CIPXQuoteRequestIntakeFormRequestQuoteResponse
  extends StandardResponse {
  data: CIPXQuoteRequestIntakeFormRequestQuoteResponseResponseData;
}

export interface CIPXQuoteRequestIntakeFormRequestQuoteResponseResponseData {
  quote_request: CIPXQuoteRequestIntakeFormQuoteRequestWrapper;
}

export interface CIPXQuoteRequestIntakeFormQuoteRequestWrapper {
  quote_request: CIPXQuoteRequestIntakeFormQuoteRequest;
}

export interface CIPXQuoteRequestIntakeFormQuoteRequest {
  quote_request_id: number;
  order_number: string;
  user_id: number;
  quotes?: CIPXQuoteRequestIntakeFormQuote[];
}

export interface CIPXQuoteRequestIntakeFormQuote {
  quote_id?: number;
  premium?: number;
}

export interface CIPXQuoteRequestIntakeFormPricingCalculatorResponse
  extends StandardResponse {
  data: CIPXQuoteRequestIntakeFormPricingCalculatorResponseData;
}

export interface CIPXQuoteRequestIntakeFormPricingCalculatorResponseData {
  pricing_configuration: CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration;
}

export interface CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration {
  penalty: CIPXQuoteRequestIntakeFormPricingCalculatorResponseDataPenalty;
  required_questions: string[];
  required_additional_questions: string[];
}

interface CIPXQuoteRequestIntakeFormPricingCalculatorResponseDataPenalty {
  penalty_input_type: number;
  pricing_model: CIPXIntakeFormPricingModel;
  default_penalty?: number;
  options?: CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption[];
}

export interface CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption {
  term: {
    years: number;
  };
  price: {
    type: string; // e.g., "fixed-amount"
    amount: number;
  };
  penalty: {
    type: string; // e.g., "fixed-amount"
    amount: number;
  };
}

// Principals API types
export interface CIPXQuoteRequestIntakeFormPrincipalsResponse extends StandardResponse {
  data: CIPXQuoteRequestIntakeFormPrincipalsResponseData;
}

export interface CIPXQuoteRequestIntakeFormPrincipalsResponseData {
  principals: CIPXQuoteRequestIntakeFormPrincipalRecord[];
}

export interface CIPXQuoteRequestIntakeFormPrincipalRecord {
  account_user_access_right_id?: number;
  account_user_id?: number;
  account_id?: number;
  access_entity_id?: number;
  access_entity_type_key?: string;
  access_flags?: number;
  is_current?: boolean;
  quote_request_id?: string;
  company_name?: string;
  company_dba?: string;
  company_number_owners?: string | null;
  applicant_type_id?: string | null;
  company_type_id?: string | null;
  user_id?: string;
}

// Principal detail by account_user_access_right_id
export interface CIPXQuoteRequestIntakeFormPrincipalDetailResponse extends StandardResponse {
  data: {
    principal: CIPXQuoteRequestIntakeFormPrincipalDetail;
  };
}

export interface CIPXQuoteRequestIntakeFormPrincipalDetailOwner {
  first_name: string;
  middle_name: string;
  last_name: string;
  ssn: string;
  spouse_ssn: string;
  marital_status_id: string | null;
  spouse_first_name: string;
  spouse_last_name: string;
  email: string;
  spouse_email: string;
  phone: string;
  years_experience: string | null;
  percent_owned: string | null;
  dob: string | null;
  home_address_street_1: string | null;
  home_address_street_2: string | null;
  home_address_city: string | null;
  home_address_state_abbreviation: string | null;
  home_address_zip: string | null;
  home_address_county: string | null;
  home_address_phone: string | null;
}

export interface CIPXQuoteRequestIntakeFormPrincipalDetail {
  owners: CIPXQuoteRequestIntakeFormPrincipalDetailOwner[];
  company_name: string;
  company_dba: string;
  company_number_owners: number;
  /** @deprecated replaced by company_formation_date */
  company_years_in_business?: number | null;
  company_formation_date?: string | null;
  applicant_type_id: string;
  company_type_id: string;
  principal_title: string;
  company_address_id: string;
  company_address_street_1?: string | null;
  company_address_street_2?: string | null;
  company_address_city?: string | null;
  company_address_state_abbreviation?: string | null;
  company_address_zip?: string | null;
  company_address_county?: string | null;
  user_id?: string | null;
  communication?: {
    communication_preferences: CIPXQuoteRequestIntakeFormCommunicationPreference[];
    principal_contacts: CIPXQuoteRequestIntakeFormPrincipalContact[];
  };
}
